import { applyTransitionConfig, initialState, transition } from './transition';
import {
  DONE_EVENT,
  ERROR_EVENT,
  type EventObject,
  type Implementations,
  type State,
  type StateMachine,
} from './types';
import { validateImplementations } from './validate';

export type Actor<C, V extends string, E extends EventObject> = {
  start: () => Actor<C, V, E>;
  stop: () => void;
  send: (event: E | E['type']) => void;
  getSnapshot: () => State<C, V>;
  /** The initial snapshot — used as the SSR/server snapshot by `useStateMachine`. */
  getInitialSnapshot: () => State<C, V>;
  subscribe: (listener: (state: State<C, V>) => void) => () => void;
};

/**
 * Interpret a machine into a running actor. Owns the async lifecycle:
 * runs a state's `invoke` on entry, aborts + invalidates it on exit/stop, and
 * ignores stale settlements so a run from a state we've left can never transition.
 * `start`/`stop` are idempotent (safe under React StrictMode double-mount).
 *
 * Fail-fast: `start()` validates every `invoke.src` and guard name in the
 * config against `impls` and throws listing what's missing. Creating an actor
 * without impls is fine for snapshot-only use (SSR).
 *
 * All transitions — external `send`s and invoke settlements — run through one
 * internal queue, so a re-entrant `send` from a subscriber is deferred until
 * the in-progress transition (including starting the next invoke) completes.
 */
export function interpret<C, V extends string, E extends EventObject>(
  machine: StateMachine<C, V>,
  impls: Implementations<C, E> = {}
): Actor<C, V, E> {
  const snapshot0 = initialState(machine, impls.input);
  let current = snapshot0;
  let started = false;
  let runId = 0;
  let invokeSettled = false;
  let controller: AbortController | null = null;
  const listeners = new Set<(state: State<C, V>) => void>();
  type QueueItem =
    | { kind: 'event'; event: E }
    | { kind: 'settle'; which: 'onDone' | 'onError'; event: E; run: number };
  const queue: QueueItem[] = [];
  let processing = false;

  function notify() {
    for (const listener of listeners) listener(current);
  }

  function abortInvoke() {
    runId += 1; // invalidate any in-flight run
    if (controller) {
      controller.abort();
      controller = null;
    }
  }

  function startInvoke() {
    const node = machine.states[current.value];
    if (!node?.invoke || invokeSettled) return;
    const service = impls.services?.[node.invoke.src];
    if (!service) {
      // Unreachable after start()'s validation — kept as an invariant guard.
      throw new Error(
        `[@heidi/flow] Service "${node.invoke.src}" has no implementation.`
      );
    }
    const myRun = runId;
    const context = current.context;
    controller = new AbortController();
    const { signal } = controller;
    // Defer the call so a *synchronous* throw from `service` becomes a rejection
    // (routed to onError) instead of escaping; skip the call entirely if the run
    // is already stale.
    Promise.resolve()
      .then(() => (myRun === runId ? service(context, { signal }) : undefined))
      .then(
        (data) => {
          if (started && myRun === runId) {
            invokeSettled = true;
            queue.push({
              kind: 'settle',
              which: 'onDone',
              run: myRun,
              event: { type: DONE_EVENT, data } as unknown as E,
            });
            drain();
          }
        },
        (error: unknown) => {
          if (started && myRun === runId) {
            invokeSettled = true;
            queue.push({
              kind: 'settle',
              which: 'onError',
              run: myRun,
              event: { type: ERROR_EVENT, error } as unknown as E,
            });
            drain();
          }
        }
      )
      .catch((error: unknown) => {
        // Not a service rejection (both settlement paths are handled above) —
        // an internal error thrown while applying the settlement transition
        // (a throwing assign, subscriber, or inspect). Never classify it as
        // stale; surface it loudly.
        queueMicrotask(() => {
          throw error;
        });
      });
  }

  function completeInvoke(kind: 'onDone' | 'onError', event: E) {
    const node = machine.states[current.value];
    const next = applyTransitionConfig(
      machine,
      current,
      node?.invoke?.[kind],
      event,
      impls.guards
    );
    enter(next, event);
  }

  function enter(next: State<C, V>, event: E) {
    if (next === current) return;
    const from = current.value;
    const changedState = next.value !== current.value;
    if (changedState) {
      abortInvoke();
      invokeSettled = false;
    }
    current = next;
    notify();
    impls.inspect?.({ from, event: event.type, to: next.value }, next.context);
    if (changedState && started && !current.done) startInvoke();
  }

  function step(event: E) {
    const next = transition(machine, current, event, { guards: impls.guards });
    if (next === current) return;
    enter(next, event);
  }

  function drain() {
    if (processing) return;
    processing = true;
    let completed = false;
    try {
      while (queue.length) {
        const item = queue.shift() as QueueItem;
        if (item.kind === 'event') step(item.event);
        else if (item.run === runId) completeInvoke(item.which, item.event);
      }
      completed = true;
    } finally {
      processing = false;
      // An exception mid-drain leaves the machine indeterminate — drop the
      // unprocessed items rather than replaying them on a later, unrelated send.
      if (!completed) queue.length = 0;
    }
  }

  function send(event: E | E['type']) {
    if (!started) return;
    queue.push({
      kind: 'event',
      event: (typeof event === 'string' ? { type: event } : event) as E,
    });
    drain();
  }

  const actor: Actor<C, V, E> = {
    start() {
      if (started) return actor;
      validateImplementations(machine, impls);
      started = true;
      startInvoke();
      return actor;
    },
    stop() {
      if (!started) return;
      started = false;
      abortInvoke();
      // Drop anything still queued: a mid-drain stop() halts the loop (the
      // `while` re-checks this array), and a later start() must never replay
      // events queued before the stop.
      queue.length = 0;
    },
    send,
    getSnapshot: () => current,
    getInitialSnapshot: () => snapshot0,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
  return actor;
}
